/**
 * Utility to normalize video URLs.
 * Google Drive links are converted to /preview embed links to bypass virus scan interstitials.
 */
export const normalizeVideoUrl = (url: string | undefined): string => {
  if (!url) return '';

  // 1. Handle Google Drive
  if (url.includes('drive.google.com')) {
    const idMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (idMatch && idMatch[1]) {
      return `https://drive.google.com/file/d/${idMatch[1]}/preview`;
    }
  }

  // 2. Handle Dropbox
  if (url.includes('dropbox.com')) {
    // raw=1 is required for streaming in a video tag, dl=1 prompts a download
    return url.replace('dl=0', 'raw=1').replace('dl=1', 'raw=1');
  }

  // Return original
  return url;
};

/**
 * Helper to determine if we should render an iframe (Drive) or a video tag (Direct).
 */
export const isEmbeddable = (url: string): boolean => {
  return url.includes('drive.google.com') && url.includes('/preview');
};

/**
 * Heuristics to check if a URL points to a video source.
 * Now handles URLs with query parameters (e.g. .mp4?token=123) correctly.
 */
export const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  
  const lowerUrl = url.toLowerCase();
  
  // Specific check for Pinterest video structure if extension is missing/obscured
  if (lowerUrl.includes('pinimg.com/videos')) return true;
  
  // Remove query params to check extension
  const cleanPath = lowerUrl.split('?')[0]; 
  
  return (
    cleanPath.endsWith('.mp4') || 
    cleanPath.endsWith('.webm') || 
    cleanPath.endsWith('.mov') || 
    cleanPath.endsWith('.mkv') ||
    lowerUrl.includes('drive.google.com') || // Assume drive links in context are videos
    lowerUrl.includes('dropbox.com')
  );
};